import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { memberLoginApi } from '../../apis/api/AuthApi';
import { clearError, logout } from '../../apis/slice/AuthSlice';
import { GetCompanyListApi } from '../../apis/api/MembershipApi';
import './HowToBecomeMember.css';

const HowToBecomeMember = () => {
  const dispatch = useDispatch();
  const { loading, error: apiError, isAuthenticated } = useSelector((state) => state.auth);
  const { companyList, loading: companyLoading } = useSelector((state) => state.membership);
  
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('__all__');

  // Default logo for companies without logo
  const defaultLogo = 'data:image/svg+xml;base64,' + btoa(`
    <svg width="80" height="80" xmlns="http://www.w3.org/2000/svg">
      <rect width="80" height="80" fill="#D4AF37"/>
      <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="#FFFFFF" text-anchor="middle" dominant-baseline="middle">EFFN</text>
    </svg>
  `);

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Get membership plan name
  const getMembershipPlan = (planString) => {
    if (!planString) return 'N/A';
    const parts = planString.split(':');
    return parts.length > 1 ? parts[1] : planString;
  };

  useEffect(() => {
    // Show table and toast if authenticated
    if (isAuthenticated && !showTable) {
      setShowTable(true);
      setShowSuccessToast(true);
      
      // Hide toast after 3 seconds
      setTimeout(() => {
        setShowSuccessToast(false);
      }, 3000);
    }
  }, [isAuthenticated, showTable]);

  // Fetch company list when component mounts if user is authenticated
  useEffect(() => {
    if (isAuthenticated && showTable) {
      dispatch(GetCompanyListApi())
        .unwrap()
        .then(() => {
          // Company list loaded
        })
        .catch((companyError) => {
          console.error('Failed to load company list:', companyError);
        });
    }
  }, [isAuthenticated, showTable, dispatch]);

  useEffect(() => {
    // Sync API error with local error state
    if (apiError) {
      setError(apiError);
    }
  }, [apiError]);

  useEffect(() => {
    // Clear errors on unmount
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    dispatch(clearError());
    
    if (!form.email || !form.password) {
      setError('Please enter your email and password.');
      return;
    }

    // Dispatch the login API
    const result = await dispatch(memberLoginApi({
      email: form.email,
      password: form.password
    }));

    // Check if login was successful
    if (memberLoginApi.fulfilled.match(result)) {
      // Success - show table and toast immediately
      setShowTable(true);
      setShowSuccessToast(true);
      
      // Call GetCompanyListApi after successful login
      dispatch(GetCompanyListApi())
        .unwrap()
        .then(() => {
          // Company list loaded
        })
        .catch((companyError) => {
          console.error('GetCompanyListApi failed:', companyError);
        });
      
      // Hide toast after 3 seconds
      setTimeout(() => {
        setShowSuccessToast(false);
      }, 3000);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    setShowTable(false);
    setShowSuccessToast(false);
    setForm({ email: '', password: '' });
  };

  // If logged in, show member directory table
  if (showTable) {
    const isEmptyData = !companyList || Object.keys(companyList).length === 0;
    const countryKeys = isEmptyData ? [] : Object.keys(companyList).sort();
    const totalMembers = countryKeys.reduce((sum, c) => sum + (companyList[c]?.length || 0), 0);

    // '__all__' = show all countries, '' = none selected
    const isAll = selectedCountry === '__all__';
    const displayedMembers = isAll
      ? countryKeys.flatMap(c => companyList[c] || [])
      : selectedCountry
        ? (companyList[selectedCountry] || [])
        : [];

    const ALL_OPTION = { value: '__all__', label: 'All Countries', count: totalMembers };

    const countryOptions = [
      ALL_OPTION,
      ...countryKeys.map(c => ({ value: c, label: c, count: companyList[c].length })),
    ];

    const selectStyles = {
      control: (p, s) => ({
        ...p,
        minHeight: '48px',
        borderRadius: '10px',
        border: s.isFocused ? '2px solid var(--theme-primary)' : '2px solid var(--theme-border)',
        boxShadow: s.isFocused ? '0 0 0 3px rgba(212,175,55,0.15)' : 'none',
        backgroundColor: 'var(--theme-bg-input)',
        fontSize: '14px',
        cursor: 'pointer',
        '&:hover': { borderColor: 'var(--theme-primary)' },
      }),
      option: (p, s) => ({
        ...p,
        fontSize: '14px',
        cursor: 'pointer',
        backgroundColor: s.isSelected
          ? 'var(--theme-primary)'
          : s.isFocused
          ? 'rgba(212,175,55,0.1)'
          : 'var(--theme-bg-dropdown)',
        color: s.isSelected ? '#fff' : 'var(--theme-text-primary)',
      }),
      menu: (p) => ({ ...p, borderRadius: '10px', border: '1px solid var(--theme-border)', overflow: 'hidden', zIndex: 9999 }),
      menuPortal: (p) => ({ ...p, zIndex: 9999 }),
      placeholder: (p) => ({ ...p, color: '#9ca3af', fontSize: '14px' }),
      singleValue: (p) => ({ ...p, color: 'var(--theme-text-primary)', fontSize: '14px', fontWeight: 600 }),
      input: (p) => ({ ...p, color: 'var(--theme-text-primary)' }),
    };

    const formatOptionLabel = ({ label, count }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <span>{label}</span>
        <span style={{
          fontSize: '12px', fontWeight: 700,
          background: 'rgba(212,175,55,0.15)',
          color: 'var(--theme-primary)',
          padding: '2px 8px', borderRadius: '20px', marginLeft: '8px'
        }}>{count}</span>
      </div>
    );

    const currentOption = countryOptions.find(o => o.value === selectedCountry) || ALL_OPTION;

    return (
      <div className="member-login-page">
        <div className="container">
          <div className="member-directory-content">
            <div className="directory-header">
              <div>
                <h1>Member Directory</h1>
                <p>Connect with verified freight forwarding professionals worldwide</p>
              </div>
            </div>

            <div className="directory-stats">
              <div className="stat-item">
                <div className="stat-number">{totalMembers}</div>
                <div className="stat-label">Total Members</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{countryKeys.length}</div>
                <div className="stat-label">Countries</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{totalMembers}</div>
                <div className="stat-label">Active Members</div>
              </div>
            </div>

            {companyLoading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading member directory...</p>
              </div>
            ) : isEmptyData ? (
              <div className="no-data">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: 'var(--theme-primary)', marginBottom: '16px' }}>
                  <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                <p>No member data available.</p>
              </div>
            ) : (
              <>
                <div className="directory-country-filter">
                  <div className="directory-filter-info">
                    <span className="directory-filter-title">Filter by Country</span>
                    <span className="directory-filter-hint">{countryKeys.length} countries available</span>
                  </div>
                  <div className="directory-filter-select-wrap">
                    <Select
                      options={countryOptions}
                      value={currentOption}
                      onChange={opt => setSelectedCountry(opt ? opt.value : '__all__')}
                      placeholder="Search or select a country..."
                      isSearchable
                      styles={selectStyles}
                      formatOptionLabel={formatOptionLabel}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      noOptionsMessage={() => 'No countries found'}
                    />
                  </div>
                </div>

                <div className="directory-results-header">
                  <span className="directory-results-title">
                    {isAll ? 'All Countries' : selectedCountry}
                    <span className="directory-results-count">{displayedMembers.length} member{displayedMembers.length !== 1 ? 's' : ''}</span>
                  </span>
                </div>

                <div className="directory-table-wrapper">
                  <table className="directory-table">
                    <thead>
                      <tr>
                        <th>Logo</th>
                        <th>Company Name</th>
                        <th>Country</th>
                        <th>City</th>
                        <th>Email</th>
                        <th>Contact</th>
                        {/* <th>Membership Plan</th> */}
                        <th>Website</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedMembers.map((company) => (
                        <tr key={company.id}>
                          <td className="logo-cell">
                            <img
                              src={company.company_logo || defaultLogo}
                              alt={company.company_name}
                              onError={(e) => { e.target.src = defaultLogo; }}
                            />
                          </td>
                          <td className="company-name">{company.company_name || 'N/A'}</td>
                          <td>{company.country || 'N/A'}</td>
                          <td>{company.city || 'N/A'}</td>
                          <td className="email-cell">
                            {company.company_email
                              ? <a href={`mailto:${company.company_email}`}>{company.company_email}</a>
                              : 'N/A'}
                          </td>
                          <td>{company.contact ? `+${company.country_code || ''} ${company.contact}` : 'N/A'}</td>
                          {/* <td className="plan-cell">{getMembershipPlan(company.member_plan)}</td> */}
                          <td className="website-cell">
                            {company.website
                              ? <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer">Visit</a>
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show login form
  return (
    <div className="member-login-page">
      {/* Success Toast */}
      {showSuccessToast && (
        <div className="success-toast">
          <div className="toast-content">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>Login Successful! Loading member directory...</span>
          </div>
        </div>
      )}
      
      <div className="container">
        <div className="member-login-wrap">
          <div className="member-login-card">
            <div className="member-login-header">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <h1 className="member-login-title">Member Login</h1>
              <p className="member-login-sub">Access the EFFN member directory and network resources</p>
            </div>

            {error && (
              <div className="member-login-error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="member-login-form" noValidate>
              <div className="member-login-group">
                <label htmlFor="ml-email">Email Address</label>
                <input
                  id="ml-email"
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  autoComplete="email"
                />
              </div>

              <div className="member-login-group">
                <label htmlFor="ml-password">Password</label>
                <div className="member-login-password-wrap">
                  <input
                    id="ml-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="member-login-eye-btn"
                    onClick={() => setShowPassword(p => !p)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" className="member-login-btn" disabled={loading}>
                {loading ? <><span className="member-login-spinner" /> Signing in...</> : 'Login'}
              </button>
            </form>

            <p className="member-login-footer">
              Not a member yet? <Link to="/join-us">Apply for membership</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToBecomeMember;
